/* ==========================================================================
   LAST PALLET - commerce

   Progressive enhancement only. Every behaviour here has a working
   no-JavaScript path underneath it:

     - the product form is a real {% form 'product' %} that posts to
       /cart/add with a variant id, so it works submitted normally
     - variant selection has a <noscript> <select name="id"> fallback
     - the cart is a real form posting to /cart with updates[] fields, so
       quantity changes and removal work via its submit buttons

   What this file adds on top: switching the hidden variant id without a
   page load, keeping price/SKU/availability in sync, adding to cart via
   the Cart AJAX API, live header count, the square-footage calculator,
   and screen-reader announcements for actions that would otherwise be
   silent.
   ========================================================================== */

(function () {
  'use strict';

  /* A section can legitimately appear more than once on a page, and each
     copy emits its own <script> tag. Bind only once so listeners and
     rAF loops are never doubled up. */
  if (window.__lpCommerce) { return; }
  window.__lpCommerce = true;

  var statusEl = document.getElementById('a11y-status');

  function announce(msg) {
    if (statusEl) { statusEl.textContent = msg; }
  }

  function money(cents) {
    // Shopify money formatting is locale/shop specific. Rather than guess a
    // format string here, values rendered server-side stay authoritative and
    // this is only used for the client-side variant swap. It matches the
    // common two-decimal case and never invents a currency symbol beyond the
    // one already present in the DOM.
    return (cents / 100).toFixed(2);
  }

  function formatLike(sample, cents) {
    if (!sample) { return money(cents); }
    var symbol = sample.replace(/[\d.,\s]/g, '');
    var num = (cents / 100).toLocaleString(undefined, {
      minimumFractionDigits: 2, maximumFractionDigits: 2
    });
    return sample.trim().indexOf(symbol) === 0 ? symbol + num : num + symbol;
  }

  /* ------------------------------------------------------------------ */
  /* Product form                                                        */
  /* ------------------------------------------------------------------ */

  function initProductForm() {
    var form = document.getElementById('product-form');
    if (!form) { return; }

    var jsonEl = document.querySelector('[data-product-json]');
    var variants = [];
    if (jsonEl) {
      try { variants = JSON.parse(jsonEl.textContent); } catch (err) { variants = []; }
    }

    var idInput = form.querySelector('[data-variant-id]');
    var optionSelects = Array.prototype.slice.call(form.querySelectorAll('[data-product-option]'));
    var addBtn = form.querySelector('[data-add-to-cart]');
    var addLabel = form.querySelector('[data-add-label]');
    var statusLine = form.querySelector('[data-form-status]');
    var qtyInput = form.querySelector('[data-qty]');

    var priceEl = document.querySelector('[data-price]');
    var compareEl = document.querySelector('[data-compare]');
    var skuEl = document.querySelector('[data-sku]');
    var cartonPriceEl = document.querySelector('[data-carton-price]');

    var priceSample = priceEl ? priceEl.textContent : '';

    function currentVariant() {
      if (!variants.length) { return null; }
      if (!optionSelects.length) { return variants[0]; }
      var chosen = optionSelects.map(function (s) { return s.value; });
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var opts = [v.option1, v.option2, v.option3];
        var match = true;
        for (var j = 0; j < chosen.length; j++) {
          if (opts[j] !== chosen[j]) { match = false; break; }
        }
        if (match) { return v; }
      }
      return null;
    }

    function syncVariant() {
      var v = currentVariant();

      if (!v) {
        if (addBtn) { addBtn.disabled = true; }
        if (addLabel) { addLabel.textContent = 'Unavailable'; }
        return;
      }

      if (idInput) { idInput.value = v.id; }

      if (addBtn) { addBtn.disabled = !v.available; }
      if (addLabel) { addLabel.textContent = v.available ? 'Add to cart' : 'Sold out'; }

      if (priceEl) { priceEl.textContent = formatLike(priceSample, v.price); }
      if (cartonPriceEl) { cartonPriceEl.textContent = formatLike(priceSample, v.price); }

      if (compareEl) {
        if (v.compare_at_price && v.compare_at_price > v.price) {
          compareEl.textContent = formatLike(priceSample, v.compare_at_price);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }

      if (skuEl) { skuEl.textContent = v.sku || ''; }

      // Keep the address bar in step so a refresh or share keeps the variant.
      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    optionSelects.forEach(function (s) {
      s.addEventListener('change', syncVariant);
    });

    // Quantity stepper. Cartons are whole units - no fractional quantities.
    var up = form.querySelector('[data-qty-up]');
    var down = form.querySelector('[data-qty-down]');

    function clampQty() {
      var n = parseInt(qtyInput.value, 10);
      if (isNaN(n) || n < 1) { n = 1; }
      qtyInput.value = Math.floor(n);
    }

    if (qtyInput) {
      qtyInput.addEventListener('change', clampQty);
      qtyInput.addEventListener('blur', clampQty);
    }
    if (up) {
      up.addEventListener('click', function () {
        clampQty();
        qtyInput.value = parseInt(qtyInput.value, 10) + 1;
      });
    }
    if (down) {
      down.addEventListener('click', function () {
        clampQty();
        var n = parseInt(qtyInput.value, 10) - 1;
        qtyInput.value = n < 1 ? 1 : n;
      });
    }

    // AJAX add-to-cart. Falls back to a normal form post if fetch fails.
    form.addEventListener('submit', function (evt) {
      if (!window.fetch || !addBtn || addBtn.disabled) { return; }
      evt.preventDefault();

      if (statusLine) { statusLine.hidden = true; statusLine.className = 'form-status'; }
      addBtn.disabled = true;

      var data = new FormData(form);

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: data
      })
        .then(function (r) {
          if (!r.ok) { return r.json().then(function (e) { throw e; }); }
          return r.json();
        })
        .then(function (item) {
          announce((item.product_title || 'Item') + ' added to your cart.');

          if (document.body.dataset.cartAction === 'cart') {
            window.location.href = '/cart';
            return;
          }

          // Stay put, bump the count, slide the drawer in. Falls back to the
          // inline status line if the drawer script didn't load.
          if (window.LastPalletCart) {
            return window.LastPalletCart.refresh(true);
          }
          if (statusLine) {
            statusLine.textContent = (item.product_title || 'Item') + ' added to your cart.';
            statusLine.hidden = false;
          }
          return refreshCartCount();
        })
        .catch(function (err) {
          var msg = (err && err.description) || 'That could not be added to the cart. Please try again.';
          announce(msg);
          if (statusLine) {
            statusLine.textContent = msg;
            statusLine.className = 'form-status form-status--error';
            statusLine.hidden = false;
          }
        })
        .then(function () {
          addBtn.disabled = false;
        });
    });

    syncVariant();
    initCalculator(form, qtyInput);
  }

  /* ------------------------------------------------------------------ */
  /* Square-footage calculator                                           */
  /* ------------------------------------------------------------------ */

  function initCalculator(form, qtyInput) {
    var calc = form.querySelector('[data-calc]');
    if (!calc || !qtyInput) { return; }

    var perCarton = parseFloat(calc.getAttribute('data-sqft-per-carton'));
    var priceCents = parseFloat(calc.getAttribute('data-price-cents'));

    // Liquid already hides this block when the metafield is missing. This is
    // the second guard: a present-but-unusable value would otherwise divide
    // by zero and put a nonsense carton count in front of a buyer.
    if (!isFinite(perCarton) || perCarton <= 0) {
      calc.remove();
      return;
    }

    var sqftInput = calc.querySelector('[data-calc-sqft]');
    var wasteInput = calc.querySelector('[data-calc-waste]');
    var out = calc.querySelector('[data-calc-out]');
    var elRoom = calc.querySelector('[data-calc-room]');
    var elTotal = calc.querySelector('[data-calc-total]');
    var elCartons = calc.querySelector('[data-calc-cartons]');
    var elCovers = calc.querySelector('[data-calc-covers]');
    var elPrice = calc.querySelector('[data-calc-price]');
    var elNote = calc.querySelector('[data-calc-note]');

    var priceSample = (document.querySelector('[data-price]') || {}).textContent || '';

    function fmt(cents) {
      var symbol = priceSample.replace(/[\d.,\s]/g, '') || '$';
      return symbol + (cents / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2, maximumFractionDigits: 2
      });
    }

    function run() {
      var sqft = parseFloat(sqftInput.value);
      var waste = parseFloat(wasteInput.value);
      if (!isFinite(waste) || waste < 0) { waste = 0; }

      if (!isFinite(sqft) || sqft <= 0) {
        out.hidden = true;
        return;
      }

      var withWaste = sqft * (1 + waste / 100);
      var cartons = Math.ceil(withWaste / perCarton);
      if (cartons < 1) { cartons = 1; }
      var covers = cartons * perCarton;

      qtyInput.value = cartons;

      elRoom.textContent = Math.round(sqft) + ' sq ft';
      elTotal.textContent = Math.round(withWaste) + ' sq ft (+' + waste + '%)';
      elCartons.textContent = cartons + (cartons === 1 ? ' carton' : ' cartons');
      elCovers.textContent = Math.round(covers) + ' sq ft';
      elPrice.textContent = isFinite(priceCents) ? fmt(priceCents * cartons) : '\u2014';
      elNote.textContent = 'Rounded up to whole cartons. Quantity updated \u2014 you can still edit it by hand.';

      out.hidden = false;
      announce(cartons + ' cartons needed. Quantity updated.');
    }

    // Live as you type: the result should feel instant, not like submitting
    // a form. Debounced so it isn't recalculating on every keystroke.
    var timer = null;
    function schedule() {
      window.clearTimeout(timer);
      timer = window.setTimeout(run, 180);
    }

    [sqftInput, wasteInput].forEach(function (el) {
      if (!el) { return; }
      el.addEventListener('input', schedule);
      el.addEventListener('keydown', function (evt) {
        if (evt.key === 'Enter') { evt.preventDefault(); window.clearTimeout(timer); run(); }
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Cart                                                                */
  /* ------------------------------------------------------------------ */

  function refreshCartCount() {
    if (!window.fetch) { return Promise.resolve(); }
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var el = document.querySelector('[data-cart-count]');
        if (el) { el.textContent = cart.item_count; }
        var link = el && el.closest('a');
        if (link) { link.setAttribute('aria-label', 'Cart, ' + cart.item_count + ' items'); }
      })
      .catch(function () { /* header count just stays as rendered */ });
  }

  function initCart() {
    var form = document.querySelector('[data-cart-form]');
    if (!form) { return; }

    // Announce the result of a normal (non-AJAX) cart form submit on the
    // page it lands on, so screen readers hear that something happened.
    if (document.referrer.indexOf('/cart') !== -1) {
      announce('Cart updated.');
    }

    form.addEventListener('submit', function () {
      announce('Updating cart.');
    });
  }

  /* ------------------------------------------------------------------ */

  function boot() {
    initProductForm();
    initCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
